import { HttpClient, HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { firstValueFrom, from, map, switchMap } from 'rxjs';
import { FileUtilities } from '../../utilities/file-utilities.service';
import { getApiUrl } from './api-interceptor.interceptor';

/**
 * Keeps guide forms light and portable: on save, base64 images embedded by Form.io's "Content"
 * editor are uploaded to `/editor-images` and stored as a host-agnostic `@@SCW_API@@/editor-images/<id>`
 * placeholder; on read, that placeholder is resolved to the current API host so images keep working
 * even if the domain changes.
 */
const API_BASE_PLACEHOLDER = '@@SCW_API@@';
const EDITOR_IMAGES_PATH = '/editor-images';

const WRITE_URL_PREFIXES = ['guide-steps', 'guide-templates'];
const READ_URL_PREFIXES = ['guide-steps', 'guide-templates', 'item-guide-assignments', 'guide-histories'];

const DATA_URI_PATTERN = /data:image\/[a-zA-Z0-9.+-]+;base64,[^"'\\\s)]+/g;
const ABSOLUTE_EDITOR_IMAGE_PATTERN = /https?:\/\/[^"'\\\s]*?\/editor-images\/([\w.-]+)/g;

export const editorImageUploadInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.method === 'GET') {
    return READ_URL_PREFIXES.some((prefix) => req.url.startsWith(prefix))
      ? next(req).pipe(map((event) => resolveImagePlaceholders(event)))
      : next(req);
  }

  const isSave = req.method === 'POST' || req.method === 'PATCH';
  const isTargetedUrl = WRITE_URL_PREFIXES.some((prefix) => req.url.startsWith(prefix));

  if (!isSave || !isTargetedUrl || !req.body || typeof req.body !== 'object') {
    return next(req);
  }

  const serializedBody = JSON.stringify(req.body);

  if (!serializedBody.includes('data:image') && !serializedBody.includes(EDITOR_IMAGES_PATH)) {
    return next(req);
  }

  const http = inject(HttpClient);

  return from(persistContentImages(serializedBody, http)).pipe(
    switchMap((newBody) => next(req.clone({ body: newBody }))),
  );
};

function resolveImagePlaceholders(event: HttpEvent<unknown>): HttpEvent<unknown> {
  if (!(event instanceof HttpResponse) || !event.body || typeof event.body !== 'object') {
    return event;
  }

  const serialized = JSON.stringify(event.body);

  if (!serialized.includes(API_BASE_PLACEHOLDER)) {
    return event;
  }

  return event.clone({ body: JSON.parse(serialized.split(API_BASE_PLACEHOLDER).join(getApiUrl())) });
}

async function persistContentImages(serializedBody: string, http: HttpClient): Promise<unknown> {
  let result = serializedBody;
  const dataUris = Array.from(new Set(result.match(DATA_URI_PATTERN) ?? []));

  for (const dataUri of dataUris) {
    const placeholderUrl = await uploadDataUri(dataUri, http);
    result = result.split(dataUri).join(placeholderUrl);
  }

  // Normalise any absolute editor-image URL (legacy data or a just-resolved read) back to the placeholder.
  return JSON.parse(result.replace(ABSOLUTE_EDITOR_IMAGE_PATTERN, `${API_BASE_PLACEHOLDER}${EDITOR_IMAGES_PATH}/$1`));
}

async function uploadDataUri(dataUri: string, http: HttpClient): Promise<string> {
  const [meta, base64] = dataUri.split(',');
  const mimeType = meta.match(/data:(.*?);base64/)?.[1] ?? 'image/png';
  const extension = mimeType.split('/')[1] ?? 'png';

  const bytes = FileUtilities.fromBase64ToUint8Array(base64);
  const file = FileUtilities.fromUint8ArrayToFile(bytes, `content-image.${extension}`, mimeType);

  const formData = new FormData();
  formData.append('file', file);

  const response = await firstValueFrom(
    http.post<{ data?: { url?: string }; url?: string }>('editor-images', formData),
  );
  const relativeUrl = response?.data?.url ?? response?.url ?? '';

  if (!relativeUrl) {
    throw new Error('Image upload response did not contain a URL.');
  }

  return `${API_BASE_PLACEHOLDER}${EDITOR_IMAGES_PATH}${relativeUrl}`;
}
